import { Button, Grid } from "@mui/material";
import FaiBox from "MuiComponents/FaiBox";
import FaiTypography from "MuiComponents/FaiTypography";
import FaiButton from "MuiComponents/FaiButton";
import FaiSelect from "MuiComponents/FaiSelect";
import FormField from "components/FormField";
import BackIcon from "components/AppIcons/BackIcon";
import React, { useEffect, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { message } from "antd";
import appStore from "store/general";
import Axios from "services/api/config";
import { createCoupon } from "services/api/routes/Coupons";
import { getAllMasterCategoriesURL } from "services/api/routes/Sale";

// Admin coupons are platform-wide. "Full" = a single discount; "Upto" = tiered
// (price-threshold -> discount). Percentage types also require a max cap.
const COUPON_TYPE_OPTIONS = [
	{ value: "PLATFORM_PERCENTAGE_FULL_DISCOUNT", label: "Percentage Off" },
	{ value: "PLATFORM_FLAT_FULL_DISCOUNT", label: "Flat Amount Off" },
	{ value: "PLATFORM_PERCENTAGE_UPTO_DISCOUNT", label: "Percentage Off — Upto (tiered)" },
	{ value: "PLATFORM_FLAT_UPTO_DISCOUNT", label: "Flat Off — Upto (tiered)" },
];

const isPercentage = (t: string) => (t || "").includes("PERCENTAGE");
const isUpto = (t: string) => (t || "").endsWith("UPTO_DISCOUNT");

interface CouponValues {
	campaignName: string;
	couponCode: string;
	description: string;
	couponType: string;
	percentageFlat: number;
	minimumPrice: number;
	maximumPrice: number;
	uptoTiers: string;
	masterCategoryId: string;
	startDate: string;
	endDate: string;
}

// Backend Utils.parseLocalDate expects dd-MM-yyyy; <input type="date"> emits yyyy-MM-dd.
const toBackendDate = (iso: string): string => {
	if (!iso) return iso;
	const [y, m, d] = iso.split("-");
	return `${d}-${m}-${y}`;
};

export default function CouponDetails({ editData = false }: any) {
	const { setCouponData }: any = appStore();
	const [categories, setCategories] = useState<
		{ value: string; label: string }[]
	>([]);

	const goBack = () => setCouponData();

	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const { data } = await Axios({ ...getAllMasterCategoriesURL });
				const list = data?.data?.masterCategories || [];
				setCategories(list.map((c: any) => ({ value: c.id, label: c.name })));
			} catch (error) {
				console.error("Error fetching master categories:", error);
			}
		};
		fetchCategories();
	}, []);

	const initialValues: CouponValues = {
		campaignName: "",
		couponCode: "",
		description: "",
		couponType: "PLATFORM_PERCENTAGE_FULL_DISCOUNT",
		percentageFlat: 0,
		minimumPrice: 0,
		maximumPrice: 0,
		uptoTiers: "",
		masterCategoryId: "",
		startDate: "",
		endDate: "",
	};

	const validationSchema = Yup.object().shape({
		campaignName: Yup.string()
			.required("Coupon name is required")
			.max(40, "Coupon name should not exceed 40 characters"),
		couponCode: Yup.string().required("Coupon code is required"),
		description: Yup.string().required("Description is required"),
		couponType: Yup.string().required("Coupon type is required"),
		percentageFlat: Yup.number()
			.required("Discount is required")
			.min(1, "Discount must be greater than 0"),
		minimumPrice: Yup.number()
			.required("Minimum amount is required")
			.min(0, "Minimum amount must be 0 or more"),
		// Percentage coupons (full or upto) require a max discount cap.
		maximumPrice: Yup.number().when("couponType", {
			is: (t: string) => isPercentage(t),
			then: (s) => s.required("Maximum discount is required").min(1, "Must be greater than 0"),
			otherwise: (s) => s.notRequired(),
		}),
		// Upto coupons require the tier definition.
		uptoTiers: Yup.string().when("couponType", {
			is: (t: string) => isUpto(t),
			then: (s) => s.required("Upto tiers are required (e.g. 1000:10, 3000:20)"),
			otherwise: (s) => s.notRequired(),
		}),
		masterCategoryId: Yup.string().required("Master Category is required"),
		startDate: Yup.date().required("Start date is required"),
		endDate: Yup.date()
			.required("End date is required")
			.min(Yup.ref("startDate"), "End date must be after start date"),
	});

	const handleSubmit = async (values: CouponValues) => {
		try {
			const fd = new FormData();
			fd.append("couponType", values.couponType);
			fd.append("campaignName", values.campaignName);
			fd.append("couponCode", values.couponCode);
			fd.append("description", values.description);
			fd.append("validFromDate", toBackendDate(values.startDate));
			fd.append("validTillDate", toBackendDate(values.endDate));
			fd.append("validFromTime", "00:00");
			fd.append("validTillTime", "23:59");
			fd.append("minimumPrice", String(values.minimumPrice));
			fd.append("percentageFlat", String(values.percentageFlat));

			// Percentage coupons need a max cap.
			if (isPercentage(values.couponType)) {
				fd.append("maximumPrice", String(values.maximumPrice));
			}

			// *_UPTO_DISCOUNT coupons need uptoDetails: a JSON map of
			// price-threshold -> discount, with one tier matching the headline
			// discount. Parse "1000:10, 3000:20, 5000:30" into that.
			if (isUpto(values.couponType)) {
				const tiers: Record<string, number> = {};
				for (const part of (values.uptoTiers || "").split(",")) {
					const [threshold, discount] = part.split(":").map((s) => s.trim());
					if (!threshold || !discount || isNaN(Number(threshold)) || isNaN(Number(discount))) {
						message.error(`Invalid tier "${part.trim()}". Use threshold:discount, e.g. 1000:10`);
						return;
					}
					tiers[threshold] = Number(discount);
				}
				if (!Object.values(tiers).includes(Number(values.percentageFlat))) {
					message.error(`One tier's discount must equal the headline discount (${values.percentageFlat})`);
					return;
				}
				fd.append("uptoDetails", JSON.stringify(tiers));
			}

			fd.append("masterCategoryIds", values.masterCategoryId);

			// Content-Type for FormData is handled by the axios interceptor (it
			// strips the default JSON header so the browser sets the boundary).
			const { data } = await Axios({ ...createCoupon, data: fd });
			message.success(data?.message || "Coupon created successfully");
			goBack();
		} catch (error: any) {
			console.error("Error creating coupon:", error);
			message.error(
				error?.response?.data?.message ||
					error?.response?.data?.errors?.[0] ||
					"Failed to create coupon. Please try again."
			);
		}
	};

	const label = (text: string) => (
		<FaiBox mb={1} display="inline-block">
			<FaiTypography component="label" variant="caption" className="!text-base !font-semibold">
				{text}
			</FaiTypography>
		</FaiBox>
	);

	return (
		<FaiBox>
			<FaiBox className="flex items-center justify-between">
				<Button startIcon={<BackIcon />} onClick={goBack} className="text-theme">
					Back to Coupons
				</Button>
			</FaiBox>
			<FaiBox className="mt-4 px-2">
				<FaiTypography variant="h6" className="!text-xl !font-semibold">
					{editData ? "Coupon" : "Create Coupon"}
				</FaiTypography>
			</FaiBox>
			<FaiBox className="mt-4 px-2">
				<Formik
					initialValues={initialValues}
					validationSchema={validationSchema}
					validateOnBlur={true}
					onSubmit={handleSubmit}
				>
					{(props: FormikProps<CouponValues>) => {
						const { values, errors, touched, handleChange, handleBlur, setFieldValue } = props;
						const pct = isPercentage(values.couponType);
						const upto = isUpto(values.couponType);
						return (
							<Form>
								<Grid container spacing={2}>
									<Grid item xs={12} sm={6}>
										<FormField
											label={"Coupon Name"}
											name={"campaignName"}
											placeholder={"Enter Coupon Name"}
											value={values.campaignName}
											onChange={handleChange}
											onBlur={handleBlur}
											error={touched.campaignName && Boolean(errors.campaignName)}
											helperText={touched.campaignName && errors.campaignName}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<FormField
											label={"Coupon Code"}
											name={"couponCode"}
											placeholder={"Enter Coupon Code"}
											value={values.couponCode}
											onChange={handleChange}
											onBlur={handleBlur}
											error={touched.couponCode && Boolean(errors.couponCode)}
											helperText={touched.couponCode && errors.couponCode}
										/>
									</Grid>
									<Grid item xs={12}>
										<FormField
											label={"Description"}
											name={"description"}
											placeholder={"Enter coupon description"}
											multiline
											rows={3}
											value={values.description}
											onChange={handleChange}
											onBlur={handleBlur}
											error={touched.description && Boolean(errors.description)}
											helperText={touched.description && errors.description}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										{label("Coupon Type")}
										<FaiSelect
											placeholder={"Choose Type"}
											name={"couponType"}
											value={COUPON_TYPE_OPTIONS.find((o) => o.value === values.couponType) || null}
											onChange={(opt: any) => setFieldValue("couponType", opt ? opt.value : "")}
											options={COUPON_TYPE_OPTIONS}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										{label("Master Category")}
										<FaiSelect
											placeholder={"Choose Category"}
											name={"masterCategoryId"}
											value={categories.find((o) => o.value === values.masterCategoryId) || null}
											onChange={(opt: any) => setFieldValue("masterCategoryId", opt ? opt.value : "")}
											options={categories}
										/>
									</Grid>
									<Grid item xs={12} sm={pct ? 4 : 6}>
										<FormField
											label={pct ? (upto ? "Top Discount %" : "Discount %") : (upto ? "Top Flat Amount" : "Flat Amount")}
											name={"percentageFlat"}
											type="number"
											placeholder={"Enter discount"}
											value={values.percentageFlat}
											onChange={handleChange}
											onBlur={handleBlur}
											error={touched.percentageFlat && Boolean(errors.percentageFlat)}
											helperText={touched.percentageFlat && errors.percentageFlat}
										/>
									</Grid>
									<Grid item xs={12} sm={pct ? 4 : 6}>
										<FormField
											label={"Minimum Order Amount"}
											name={"minimumPrice"}
											type="number"
											placeholder={"Enter minimum amount"}
											value={values.minimumPrice}
											onChange={handleChange}
											onBlur={handleBlur}
											error={touched.minimumPrice && Boolean(errors.minimumPrice)}
											helperText={touched.minimumPrice && errors.minimumPrice}
										/>
									</Grid>
									{pct && (
										<Grid item xs={12} sm={4}>
											<FormField
												label={"Max Discount Amount"}
												name={"maximumPrice"}
												type="number"
												placeholder={"Enter max discount"}
												value={values.maximumPrice}
												onChange={handleChange}
												onBlur={handleBlur}
												error={touched.maximumPrice && Boolean(errors.maximumPrice)}
												helperText={touched.maximumPrice && errors.maximumPrice}
											/>
										</Grid>
									)}
									{upto && (
										<Grid item xs={12}>
											<FormField
												label={"Upto Tiers (threshold:discount, comma-separated)"}
												name={"uptoTiers"}
												placeholder={"e.g. 1000:10, 3000:20, 5000:30"}
												value={values.uptoTiers}
												onChange={handleChange}
												onBlur={handleBlur}
												error={touched.uptoTiers && Boolean(errors.uptoTiers)}
												helperText={
													(touched.uptoTiers && errors.uptoTiers) ||
													"One tier's discount must equal the headline discount above."
												}
											/>
										</Grid>
									)}
									<Grid item xs={12} sm={6}>
										<FormField
											type={"date"}
											label={"Start Date"}
											name={"startDate"}
											value={values.startDate}
											onChange={handleChange}
											onBlur={handleBlur}
											error={touched.startDate && Boolean(errors.startDate)}
											helperText={touched.startDate && errors.startDate}
										/>
									</Grid>
									<Grid item xs={12} sm={6}>
										<FormField
											type={"date"}
											label={"End Date"}
											name={"endDate"}
											value={values.endDate}
											onChange={handleChange}
											onBlur={handleBlur}
											error={touched.endDate && Boolean(errors.endDate)}
											helperText={touched.endDate && errors.endDate}
										/>
									</Grid>
								</Grid>
								<FaiBox className="flex justify-start mt-5 gap-2">
									<FaiButton type="submit" color="primary">
										Save
									</FaiButton>
									<FaiButton variant="outlined" onClick={goBack}>
										Cancel
									</FaiButton>
								</FaiBox>
							</Form>
						);
					}}
				</Formik>
			</FaiBox>
		</FaiBox>
	);
}
